import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';

const apiClient = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor — attach JWT
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — auto-refresh on an expired/missing session.
//
// TWO shapes mean "your session is gone", and they arrive with DIFFERENT
// status codes:
//   * 401 — the token was present but expired/invalid.
//   * 403 "Not authenticated" — FastAPI's HTTPBearer default when the
//     request carried NO Authorization header at all. Mobile browsers
//     evict localStorage (Safari purges script-writable storage after ~7
//     idle days, and some tab restores), so the app's in-memory state
//     says "signed in" while the tokens are gone — every request goes out
//     headerless and the operator saw a dead "Not authenticated" page
//     instead of the login screen (2026-08-02, phone).
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const detail = error.response?.data?.detail;
    const sessionGone = status === 401
      || (status === 403 && detail === 'Not authenticated');
    if (sessionGone && original && !original._retry) {
      original._retry = true;
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          window.location.replace('/login');
          return Promise.reject(error);
        }
        const res = await axios.post(`${API_URL}/api/auth/refresh`, { refresh_token: refreshToken });
        const { access_token, refresh_token: rotated } = res.data;
        localStorage.setItem('accessToken', access_token);
        // Sliding session: the server re-issues the refresh token on every
        // use, so an active device never ages out of its sign-in.
        if (rotated) localStorage.setItem('refreshToken', rotated);
        original.headers['Authorization'] = `Bearer ${access_token}`;
        return apiClient(original);
      } catch (refreshError) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.replace('/login');
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
